<template>
  <q-btn color="primary" class="wrapper" :disable="this.disabled">
    <div class="header">
      {{ this.title }} <q-icon size="2em" color="primary" name="expand_more" />
    </div>
    <q-popup-proxy ref="customDropdown" v-if="this.options?.length">
      <q-banner class="options" :style="{ width: this.width ? `${this.width}px` : 'auto' }">
        <q-linear-progress v-if="this.loading" indeterminate />
        <template v-if="this.hasGroups && this.isGrouped">
          <div
            v-for="(group, groupName) in this.groupedOptions"
            :key="groupName"
            class="options__group"
          >
            <div
              class="options__group-label"
              :class="{ 'options__group-label--static': !isCollapsible(groupName) }"
              @click.stop="isCollapsible(groupName) && toggleGroup(groupName)"
            >
              <span>
                {{ this.groupLabels ? this.groupLabels(groupName) : groupName }}
              </span>
              <q-icon
                v-if="isCollapsible(groupName)"
                class="options__group-label-icon"
                :name="isGroupOpen(groupName) ? 'expand_more' : 'chevron_right'"
                size="16px"
              />
            </div>
            <div v-if="isGroupOpen(groupName)">
              <div
                v-for="item in group"
                :key="item.action"
                :class="[
                  'options__item',
                  {
                    options__item_disabled: this.loading || item.disabled,
                    options__item_negative: item.color === 'negative' || item.color === 'red',
                  },
                ]"
                v-on:click="this.handleClick(item)"
                :title="item.disabled && item.hint ? item.hint : ''"
              >
                <q-icon
                  v-if="item.icon"
                  :name="item.icon"
                  size="18px"
                  class="options__item-icon"
                />
                {{ item.label }}
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            :class="['options__item', { 
              options__item_disabled: this.loading || item.disabled,
              options__item_negative: item.color === 'negative' || item.color === 'red'
            }]"
            v-for="item in (this.options || [])"
            v-on:click="this.handleClick(item)"
            :title="item.disabled && item.hint ? item.hint : ''"
          >
            <q-icon v-if="item.icon" :name="item.icon" size="18px" class="options__item-icon" />
            {{ item.label }}
          </div>
        </template>
      </q-banner>
    </q-popup-proxy>
  </q-btn>
</template>

<script>
export default {
  name: "CustomDropdown",
  props: ["title", "options", "onClick", "loading", "width", 'disabled', 'groupLabels', 'isGrouped'],
  data() {
    return {
      openGroups: {},
    }
  },
  computed: {
    hasGroups() {
      return this.options?.some(item => item.group)
    },
    groupedOptions() {
      if (!this.hasGroups) return {}

      const grouped = {}
      this.options.forEach(item => {
        const groupName = item.group || 'other'
        if (!grouped[groupName]) {
          grouped[groupName] = []
        }
        grouped[groupName].push(item)
      })
      
      // Сортируем группы: сначала обычные, потом delete
      const sortedGroups = {}
      const groupOrder = Object.keys(grouped).sort((a, b) => {
        if (a === 'delete') return 1
        if (b === 'delete') return -1
        return 0
      })
      
      groupOrder.forEach(groupName => {
        sortedGroups[groupName] = grouped[groupName]
        if (this.openGroups[groupName] === undefined) {
          this.openGroups[groupName] = true
        }
      })
      
      return sortedGroups
    }
  },
  methods: {
    handleClick(item) {
      if (!this.onClick || this.loading || item?.disabled) return
      this.onClick(item)
      if (item?.hideWhenClick) {
        this.$refs.customDropdown?.hide();
      }
    },
    isCollapsible(groupName) {
      // Сворачиваем только Операции и Смету, Договора фиксированы
      const collapsible = ['operations', 'smeta']
      return collapsible.includes(groupName)
    },
    toggleGroup(groupName) {
      if (!this.isCollapsible(groupName)) return

      const current = this.openGroups[groupName]
      this.openGroups[groupName] = !current
    },
    isGroupOpen(groupName) {
      if (!this.isCollapsible(groupName)) {
        return true
      }

      if (this.openGroups[groupName] === undefined) {
        return true
      }
      return this.openGroups[groupName]
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  border-radius: 3px;
  border: 1px solid #183FA6 !important;
  background: #FFF !important;
  color: #183FA6 !important;
  padding: 2px 7px !important;
  min-height: auto !important;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &>span {
    background: #FFF !important;
  }

  &:before {
    box-shadow: none;
  }

  &:hover {
    background: #FFF !important;

    span {
      background: #FFF !important;
    }
  }
}

.header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.options {
  padding: 5px 0;
  min-height: auto;

  &__group {
    &:not(:last-child) {
      border-bottom: 1px solid #e0e0e0;
      margin-bottom: 8px;
      padding-bottom: 8px;
    }

    &:not(:first-child) {
      margin-top: 8px;
    }

    // Для группы удаления добавляем дополнительный отступ сверху
    &:has(.options__item_negative) {
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px solid #ffcdd2;
    }
  }

  &__group-label {
    font-size: 11px;
    font-weight: 600;
    color: #183FA6;
    padding: 6px 10px 4px 10px;
    text-transform: uppercase;
    background: linear-gradient(90deg, #f0f4ff 0%, transparent 100%);
    border-radius: 3px;
    margin-bottom: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  &__group-label--static {
    cursor: default;
  }

  &__group-label-icon {
    flex-shrink: 0;
    color: #183FA6;
  }

  &__item {
    transition: all .3s;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 12px;
    border-radius: 3px;
    margin: 2px 5px;
    display: flex;
    align-items: center;
    gap: 8px;

    &-icon {
      flex-shrink: 0;
    }

    &:hover {
      background: #f0f4ff;
      color: #183FA6;
      transform: translateX(2px);
    }

    &_disabled {
      cursor: default;
      background: #e4e4e4;
      color: #515151;

      &:hover {
        background: #e4e4e4;
        transform: translateX(0);
      }
    }

    &_negative {
      color: #f44336;

      &:hover {
        background: #ffebee;
        color: #d32f2f;
      }
    }
  }
}
</style>
